import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormField, FormInput, FormSelect, FormSectionTitle, RowActionButton } from '@practium/ui';
import type { SystemPageMode, PlanFormData, PaymentMethod } from '../../../types';
import {
	PAYMENT_METHOD_OPTIONS,
	RECURRING_OPTIONS,
	BOOL_OPTIONS,
	INTERVAL_OPTIONS,
	IS_LISTABLE_OPTIONS,
} from '../../../constants/system.constants';
import { usePlanForm } from '../../../hooks/usePlanForm';
import styles from '../SystemForm.module.css';

const PAYMENT_LABELS: Record<string, string> = {
	boleto: 'Boleto',
	credit_card: 'Cartão de Crédito',
	free: 'Gratuito',
};

const RECURRING_LABELS: Record<string, string> = { yes: 'Sim', no: 'Não' };
const INTERVAL_TYPE_LABELS: Record<string, string> = {
	day: 'Dia',
	week: 'Semana',
	month: 'Mês',
	year: 'Ano',
};
const LANDING_LABELS: Record<string, string> = { yes: 'Ativo', no: 'Inativo' };

export interface PlanFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface PlanFormProps {
	id?: string;
	mode: SystemPageMode;
	initialData?: PlanFormData;
	onSubmit: () => void;
	onLoadingChange: (loading: boolean) => void;
	onFormChange?: (data: PlanFormData) => void;
}

export const PlanForm = forwardRef<PlanFormRef, PlanFormProps>(function PlanForm(
	{ id, mode, initialData, onSubmit, onLoadingChange, onFormChange },
	ref
) {
	const {
		form,
		advantageKeys,
		errors,
		isLoading,
		hierarchyOptions,
		isFree,
		hasCard,
		set,
		setPaymentMethods,
		setAdvantage,
		addAdvantage,
		removeAdvantage,
		handleSubmit,
		isDirty,
	} = usePlanForm({ id, initialData, onSubmit });

	useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

	useEffect(() => {
		onLoadingChange(isLoading);
	}, [isLoading, onLoadingChange]);

	useEffect(() => {
		onFormChange?.(form);
	}, [form, onFormChange]);

	const isView = mode === 'view';

	if (isView) {
		return (
			<div className={styles.wrapper}>
				<FormSectionTitle>INDENTIFICAÇÃO</FormSectionTitle>
				<div className={styles.viewGrid}>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Nome</span>
						<span className={styles.viewValue}>{form.name || '—'}</span>
					</div>
					<div className={`${styles.viewField} ${styles.fullWidth}`}>
						<span className={styles.viewLabel}>Descrição</span>
						<span className={styles.viewValue}>{form.description || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Opções de Pagamento</span>
						<span className={styles.viewValue}>
							{form.paymentMethods.length
								? form.paymentMethods.map(m => PAYMENT_LABELS[m] ?? m).join(', ')
								: '—'}
						</span>
					</div>
					{!isFree && (
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Valor</span>
							<span className={styles.viewValue}>{form.price || '—'}</span>
						</div>
					)}
					{!isFree && (
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Pagamento Recorrente</span>
							<span className={styles.viewValue}>{RECURRING_LABELS[form.recurrentPayment] || '—'}</span>
						</div>
					)}
					{!isFree && (
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Intervalo</span>
							<span className={styles.viewValue}>{form.intervalCount || '—'}</span>
						</div>
					)}
					{!isFree && (
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Tipo de Intervalo</span>
							<span className={styles.viewValue}>
								{INTERVAL_TYPE_LABELS[form.interval] || '—'}
							</span>
						</div>
					)}
					{!isFree && (
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Dias de Inadimplência</span>
							<span className={styles.viewValue}>{form.gracePeriod || '—'}</span>
						</div>
					)}
					{!isFree && (
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Notificar Inadimplência</span>
							<span className={styles.viewValue}>{RECURRING_LABELS[form.notifyUnpayment] || '—'}</span>
						</div>
					)}
					{!isFree && (
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Notificar Nota Fiscal</span>
							<span className={styles.viewValue}>{RECURRING_LABELS[form.notifyInvoice] || '—'}</span>
						</div>
					)}
					{hasCard && (
						<div className={styles.viewField}>
							<span className={styles.viewLabel}>Parcelas Máximas</span>
							<span className={styles.viewValue}>{form.maxInstallments || '—'}</span>
						</div>
					)}
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Qtd. Máxima de Propriedades</span>
						<span className={styles.viewValue}>{form.maxProps || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Qtd. Máxima de Recomendações</span>
						<span className={styles.viewValue}>{form.maxSamples || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Tipo Hierárquico</span>
						<span className={styles.viewValue}>
							{hierarchyOptions.find(o => o.value === form.hierarchy)?.label || form.hierarchy || '—'}
						</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Tokens Mensais para Flora</span>
						<span className={styles.viewValue}>{form.monthlyFloraTokens || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Landing Page</span>
						<span className={styles.viewValue}>{LANDING_LABELS[form.isListable] || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Link de Pagamento</span>
						{form.paymentLink ? (
							<a
								href={form.paymentLink}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.viewLink}
							>
								{form.paymentLink}
							</a>
						) : (
							<span className={styles.viewValue}>—</span>
						)}
					</div>

					<hr className={styles.sectionDivider} />
					<div className={styles.fullWidth}>
						<FormSectionTitle>VANTAGENS</FormSectionTitle>
					</div>
					<div className={styles.listSection}>
						{form.advantages
							.filter((a) => a.trim())
							.map((a, i) => (
								<div key={`${i}-${a}`} className={styles.listRowView}>
									<div className={styles.viewField}>
										<span className={styles.viewLabel}>Vantagem {i + 1}</span>
										<span className={styles.viewValue}>{a}</span>
									</div>
								</div>
							))}
						{form.advantages.filter((a) => a.trim()).length === 0 && (
							<div className={styles.viewField}>
								<span className={styles.viewValue}>—</span>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<FormSectionTitle>INDENTIFICAÇÃO</FormSectionTitle>
			<div className={styles.formGrid}>
				<FormField label="Nome" required error={errors.name}>
					<FormInput
						value={form.name}
						onChange={(e) => set('name', e.target.value)}
						placeholder="Ex.: Plano Profissional"
						hasError={!!errors.name}
					/>
				</FormField>
				<div className={styles.fullWidth}>
					<FormField label="Descrição" required error={errors.description}>
						<textarea
							className={styles.textarea}
							value={form.description}
							onChange={(e) => set('description', e.target.value)}
							placeholder="Ex.: Ideal para quem está começando a organizar a produção."
							rows={4}
						/>
					</FormField>
				</div>
				<FormField label="Opções de Pagamento" required error={errors.paymentMethods}>
					<FormSelect
						multiple
						clearable
						value={form.paymentMethods}
						onChange={(v) => {
							const next = v as PaymentMethod[];
							if (next.includes('free') && !form.paymentMethods.includes('free')) {
								setPaymentMethods(['free']);
							} else {
								setPaymentMethods(next.filter((m) => m !== 'free'));
							}
						}}
						options={PAYMENT_METHOD_OPTIONS}
						placeholder="Selecione as opções de pagamento"
						hasError={!!errors.paymentMethods}
					/>
				</FormField>
				{!isFree && (
					<FormField label="Valor" required error={errors.price}>
						<FormInput
							value={form.price}
							onChange={(e) => set('price', e.target.value)}
							placeholder="Ex.: 500,00"
							hasError={!!errors.price}
						/>
					</FormField>
				)}
				{!isFree && (
					<FormField label="Pagamento Recorrente" required error={errors.recurrentPayment}>
						<FormSelect
							value={form.recurrentPayment}
							onChange={(v) => set('recurrentPayment', v)}
							options={RECURRING_OPTIONS}
							placeholder="Selecione se é recorrente"
							hasError={!!errors.recurrentPayment}
						/>
					</FormField>
				)}
				{!isFree && (
					<FormField label="Intervalo" required error={errors.intervalCount}>
						<FormInput
							value={form.intervalCount}
							onChange={(e) => set('intervalCount', e.target.value)}
							placeholder="Ex.: 1"
							hasError={!!errors.intervalCount}
						/>
					</FormField>
				)}
				{!isFree && (
					<FormField label="Tipo de Intervalo" required error={errors.interval}>
						<FormSelect
							value={form.interval}
							onChange={(v) => set('interval', v as PlanFormData['interval'])}
							options={INTERVAL_OPTIONS}
							placeholder="Selecione o tipo de intervalo"
							hasError={!!errors.interval}
						/>
					</FormField>
				)}
				{!isFree && (
					<FormField label="Dias de Inadimplência" required error={errors.gracePeriod}>
						<FormInput
							value={form.gracePeriod}
							onChange={(e) => set('gracePeriod', e.target.value)}
							placeholder="Ex.: 7"
							hasError={!!errors.gracePeriod}
						/>
					</FormField>
				)}
				{!isFree && (
					<FormField label="Notificar Inadimplência" error={errors.notifyUnpayment}>
						<FormSelect
							value={form.notifyUnpayment}
							onChange={(v) => set('notifyUnpayment', v)}
							options={BOOL_OPTIONS}
							placeholder="Selecione uma opção"
							hasError={!!errors.notifyUnpayment}
						/>
					</FormField>
				)}
				{!isFree && (
					<FormField label="Notificar Nota Fiscal" error={errors.notifyInvoice}>
						<FormSelect
							value={form.notifyInvoice}
							onChange={(v) => set('notifyInvoice', v)}
							options={BOOL_OPTIONS}
							placeholder="Selecione uma opção"
							hasError={!!errors.notifyInvoice}
						/>
					</FormField>
				)}
				{hasCard && (
					<FormField label="Parcelas Máximas" required error={errors.maxInstallments}>
						<FormInput
							value={form.maxInstallments}
							onChange={(e) => set('maxInstallments', e.target.value)}
							placeholder="Ex.: 12"
							hasError={!!errors.maxInstallments}
						/>
					</FormField>
				)}
				<FormField label="Quantidade Máxima de Propriedades" required error={errors.maxProps}>
					<FormInput
						value={form.maxProps}
						onChange={(e) => set('maxProps', e.target.value)}
						placeholder="Ex.: 20"
						hasError={!!errors.maxProps}
					/>
				</FormField>
				<FormField
					label="Quantidade Máxima de Recomendações"
					required
					error={errors.maxSamples}
				>
					<FormInput
						value={form.maxSamples}
						onChange={(e) => set('maxSamples', e.target.value)}
						placeholder="Ex.: 10"
						hasError={!!errors.maxSamples}
					/>
				</FormField>
				<FormField label="Tipo Hierárquico" required error={errors.hierarchy}>
					<FormSelect
						value={form.hierarchy}
						onChange={(v) => set('hierarchy', v)}
						options={hierarchyOptions}
						placeholder="Selecione o tipo hierárquico"
						hasError={!!errors.hierarchy}
					/>
				</FormField>
				<FormField label="Tokens Mensais para Flora" error={errors.monthlyFloraTokens}>
					<FormInput
						value={form.monthlyFloraTokens}
						onChange={(e) => set('monthlyFloraTokens', e.target.value)}
						placeholder="Ex.: 500"
						hasError={!!errors.monthlyFloraTokens}
					/>
				</FormField>
				<FormField label="Landing Page">
					<FormSelect
						value={form.isListable}
						onChange={(v) => set('isListable', v)}
						options={IS_LISTABLE_OPTIONS}
						placeholder="Selecione o status"
					/>
				</FormField>
				<hr className={styles.sectionDivider} />
				<div className={styles.fullWidth}>
					<FormSectionTitle>VANTAGENS</FormSectionTitle>
				</div>
				<div className={styles.listSection}>
					{advantageKeys.map((key, index) => (
						<div key={key} className={styles.listRow}>
							<FormField label={`Vantagem ${index + 1}`}>
								<FormInput
									value={form.advantages[index] ?? ''}
									onChange={(e) => setAdvantage(index, e.target.value)}
									placeholder={`Ex.: Vantagem ${index + 1}`}
								/>
							</FormField>
							<div className={styles.listRowAction}>
								{index === 0 && <RowActionButton variant="add" onClick={addAdvantage} />}
								{advantageKeys.length > 1 && <RowActionButton variant="delete" onClick={() => removeAdvantage(index)} />}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
});
